/**
 * SUPPORT AND DISPUTES. UF-07.
 *
 * The blocked-payment screen has been telling people, in my own words, that they could raise a dispute
 * "once the support panel is built". This is that panel, and the thing it is built on did not exist
 * either: there was no dispute domain at all.
 *
 * ## The one thing this screen must get right
 *
 * A person raising a dispute is having the worst day they will ever have with this product. Their
 * money is stuck, or gone, or somebody else is in their account. They are frightened and they do not
 * want to choose a category from a dropdown.
 *
 * So the categories are written as SENTENCES A PERSON WOULD SAY, not as taxonomy. "Somebody used my
 * account" is a category because that is what a person types into a search box at 2am. And the
 * urgency comes from the category, silently — they do not have to argue for it, and they do not have
 * to know that arguing for it is possible.
 */

import { useCallback, useEffect, useState } from 'react';
import { fmtDateTime } from '@/lib/format';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { api, LipaApiError, type Dispute, type DisputeCategory } from '@/lib/api';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { StatusBadge } from '@/components/dispute';
import { AssistantLauncher } from '@/components/assistant';

/**
 * The categories, in the words a person in trouble would use.
 *
 * The `urgent` note is shown so they know we heard them. A person who reports that somebody is in
 * their account and gets back a cheerful "we will respond within 3 working days" has learned that we
 * did not understand what they said.
 */
const CATEGORIES: { value: DisputeCategory; label: string; note?: string }[] = [
  {
    value: 'unauthorized_transaction',
    label: 'Somebody used my account',
    note: 'We treat this as urgent and look at it within 4 hours.',
  },
  {
    value: 'escrow_not_released',
    label: 'My money is stuck in escrow',
    note: 'We look at this within 12 hours.',
  },
  {
    value: 'account_frozen',
    label: 'My account is frozen and I do not know why',
    note: 'We look at this within 12 hours.',
  },
  { value: 'payment_not_received', label: 'I paid somebody and they say it never arrived' },
  { value: 'wrong_amount', label: 'The wrong amount was taken' },
  { value: 'verification_rejected', label: 'My verification was rejected and I think it is wrong' },
  { value: 'tokenization_issue', label: 'Something is wrong with one of my assets' },
  { value: 'other', label: 'Something else' },
];

export default function Support() {
  // React Router returns a [params, setParams] tuple where Next returned the params directly.
  const [params] = useSearchParams();

  // A dispute raised FROM a blocked payment arrives with its reference already attached. The person
  // should not have to go and find it, and an officer should not have to ask them for it.
  const about = params.get('about');

  const [cases, setCases] = useState<Dispute[]>([]);
  //: Needed only so the drawer can record the assisted-help consent against the right participant.
  //: The ASK path takes the principal from the session token and never from here, which is why this
  //: being null degrades the drawer to read-only rather than letting anybody consent as anybody.
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [raising, setRaising] = useState(Boolean(about));

  const load = useCallback(async () => {
    setError(null);
    try {
      setCases(await api.disputes());
      // Best effort. A failure here must not take the support screen down: disputes are the thing
      // that matters on this page and assisted help is the optional extra.
      void api
        .me()
        .then((m) => setParticipantId(m.participant_id))
        .catch(() => setParticipantId(null));
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load your cases.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingCard lines={3} />;

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div className="flex items-baseline justify-between">
        <h1 className="text-page-title text-ink">Support</h1>
        <div className="flex items-center gap-2">
          {/* Assisted help sits BESIDE reporting a problem, never in front of it. Somebody whose
              money is missing should not have to talk to an assistant first. */}
          <AssistantLauncher participantId={participantId} />
          {!raising ? (
            <button
              onClick={() => setRaising(true)}
              className="rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
            >
              Report a problem
            </button>
          ) : null}
        </div>
      </div>

      {error ? <ErrorState body={error} onRetry={() => void load()} /> : null}

      {raising ? (
        <RaiseForm
          about={about}
          onDone={async () => {
            setRaising(false);
            await load();
          }}
          onCancel={() => setRaising(false)}
        />
      ) : null}

      {cases.length === 0 && !raising ? (
        <EmptyState
          title="You have not reported anything"
          body="If money goes missing, an account is frozen, or something is not right, tell us here and we will investigate."
          action={{ label: 'Report a problem', onClick: () => setRaising(true) }}
        />
      ) : null}

      {cases.length > 0 ? (
        <ul className="space-y-3">
          {cases.map((c) => (
            <li key={c.case_id}>
              <Link
                to={`/support/${c.case_id}`}
                className="block rounded-card border border-border bg-surface p-4 shadow-card transition hover:border-primary/40 hover:shadow-soft"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                    {c.subject}
                  </p>
                  <StatusBadge status={c.status} />
                </div>

                {c.status === 'resolved' ? (
                  <p className="mt-1 line-clamp-2 text-sm text-ink-muted">{c.resolution}</p>
                ) : (
                  // THE PROMISE, on the card. A person whose money is stuck wants to know when
                  // somebody will look at it, and "we have received your case" is not that.
                  <p className="mt-1 text-sm text-ink-muted">
                    We will get back to you by {fmtDateTime(c.sla_due_at)}.
                  </p>
                )}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}

      {/* WAYS TO REACH US (FR-U-DSP-003). A dispute is the formal, tracked path; these are the human
          ones. The in-app channel is the durable, real one; email and SMS are simulated in the sandbox
          (ADR 0003), so we do not promise a reply on a channel that cannot yet send. */}
      {!raising ? (
        <section className="rounded-card border border-border bg-surface p-5 shadow-card">
          <h2 className="font-semibold text-ink">Other ways to reach us</h2>
          <ul className="mt-3 space-y-3 text-sm">
            <ContactRow
              label="Help centre"
              value="Browse answers to common questions"
              icon="M8 10.5h8M8 14h5m-8.5 5.5 2.2-2.2A2 2 0 0 1 8.1 16.7H17a2.5 2.5 0 0 0 2.5-2.5v-6A2.5 2.5 0 0 0 17 5.7H7A2.5 2.5 0 0 0 4.5 8.2v11.3Z"
            />
            <ContactRow
              label="Email"
              value="support@lipa.pocket"
              icon="M4 7.5A1.5 1.5 0 0 1 5.5 6h13A1.5 1.5 0 0 1 20 7.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 16.5v-9Zm0 .5 8 5 8-5"
            />
            <ContactRow
              label="Phone"
              value="0800 000 000 (toll free)"
              icon="M6.5 4h3l1.5 4-2 1.5a12 12 0 0 0 5 5l1.5-2 4 1.5v3a2 2 0 0 1-2 2A16 16 0 0 1 4.5 6a2 2 0 0 1 2-2Z"
            />
          </ul>
          <p className="mt-3 text-xs text-ink-subtle">
            For anything about money that has moved, open a report above. It is tracked, time-bound,
            and recorded permanently.
          </p>
        </section>
      ) : null}
    </div>
  );
}

function ContactRow({ label, value, icon }: { label: string; value: string; icon: string }) {
  return (
    <li className="flex items-center gap-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-brand">
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={icon} />
        </svg>
      </span>
      <div className="min-w-0">
        <p className="font-medium text-ink">{label}</p>
        <p className="truncate text-ink-muted">{value}</p>
      </div>
    </li>
  );
}

function RaiseForm({
  about,
  onDone,
  onCancel,
}: {
  about: string | null;
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  const [category, setCategory] = useState<DisputeCategory>('unauthorized_transaction');
  const [subject, setSubject] = useState('');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answerBy, setAnswerBy] = useState<string | null>(null);

  const chosen = CATEGORIES.find((c) => c.value === category);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const result = await api.raiseDispute({
        category,
        subject,
        detail,
        correlation_id: about ?? undefined,
      });

      // Tell them WHEN. Immediately, before anything else. It is the only thing they want to know.
      setAnswerBy(result.we_will_answer_by);
      setTimeout(() => void onDone(), 2500);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not log that.');
    } finally {
      setBusy(false);
    }
  }

  if (answerBy) {
    return (
      <div className="rounded-card border border-accent/30 bg-accent/5 p-6 text-center">
        <p className="font-semibold text-accent">We have it.</p>
        <p className="mt-1 text-sm text-ink">We will get back to you by {fmtDateTime(answerBy)}.</p>
        <p className="mt-2 text-xs text-ink-muted">
          Your case is logged permanently and cannot be deleted, including by us.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4 rounded-card border border-border bg-surface p-5">
      <h2 className="font-medium text-ink">What has happened?</h2>

      {error ? (
        <div
          role="alert"
          className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
        >
          {error}
        </div>
      ) : null}

      <div>
        <label htmlFor="category" className="block text-sm font-medium text-ink">
          Choose the closest
        </label>
        <select
          id="category"
          value={category}
          onChange={(e) => setCategory(e.target.value as DisputeCategory)}
          className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2.5 text-base text-ink outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
        >
          {CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>

        {/* We heard you. A person reporting that somebody is in their account and getting back "3
            working days" has learned that we did not understand what they said. */}
        {chosen?.note ? (
          <p className="mt-1.5 text-xs font-medium text-brand">{chosen.note}</p>
        ) : null}
      </div>

      <div>
        <label htmlFor="subject" className="block text-sm font-medium text-ink">
          In one line
        </label>
        <input
          id="subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Money left my account on Tuesday and I did not send it"
          required
          minLength={3}
          className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2.5 text-base text-ink outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
        />
      </div>

      <div>
        <label htmlFor="detail" className="block text-sm font-medium text-ink">
          Tell us everything
        </label>
        <textarea
          id="detail"
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          rows={5}
          required
          minLength={10}
          placeholder="What happened, when, and anything else you think matters."
          className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2.5 text-base text-ink outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
        />
        <p className="mt-1 text-xs text-ink-muted">
          You can add photos or screenshots once the case is open.
        </p>
      </div>

      {about ? (
        <p className="rounded-lg bg-canvas p-3 font-mono text-micro text-ink-muted">
          About payment {about}
        </p>
      ) : null}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 rounded-field border border-border py-3 text-sm font-medium text-ink transition hover:bg-surface-sunken"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy}
          className="flex-1 rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:opacity-50"
        >
          {busy ? 'Logging…' : 'Report it'}
        </button>
      </div>
    </form>
  );
}
